import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Clipboard } from '@angular/cdk/clipboard';
import { of } from 'rxjs';
import { switchMap, mergeMap, map, catchError, filter, take, tap } from 'rxjs/operators';
import { ZefSnackService } from '@zerops/zef/snack';
import {
  getFeatureNameWithId,
  onWebsocketMessageDispatchUpdateEntities,
  successOf
} from '@zerops/zef/entities';
import { UserEntity } from '../user-base/user-base.entity';
import { UserDataEntity } from './user-data-base.entity';
import { UserDataBaseApi } from './user-data-base.api';
import {
  sync,
  syncSuccess,
  syncFail,
  updateSecretEnvs,
  updateSecretEnvsSuccess,
  updateSecretEnvsFail,
  loadUserDataEnvFile,
  loadUserDataEnvFileSuccess,
  loadUserDataEnvFileFail,
  revealUserData,
  revealUserDataSuccess,
  revealUserDataFail
} from './user-data-base.action';

@Injectable()
export class UserDataBaseEffect {

  private _snack = inject(ZefSnackService);
  private _clipboard = inject(Clipboard);

  onSync$ = createEffect(() => this._actions$.pipe(
    ofType(sync),
    switchMap((action) => this._api
      .sync$(action.data)
      .pipe(
        map((res) => syncSuccess(res, action)),
        catchError((res) => of(syncFail(res, action)))
      )
    )
  ));

  onDeleteSecretEnvSuccess$ = createEffect(() => this._actions$.pipe(
    successOf(this._userDataEntity.deleteOne),
    filter(({ originalAction }) => !!originalAction?.meta?.removeTag),
    map(({ originalAction }) => this._userDataEntity.removeIdsFromCache(
      [ originalAction.data.id ],
      {
        tag: getFeatureNameWithId(originalAction.meta.removeTag),
        handleGlobally: true
      }
    ))
  ));

  onUpdateSecretEnvs$ = createEffect(() => this._actions$.pipe(
    ofType(updateSecretEnvs),
    switchMap((action) => this._api
      .updateSecretEnvs$(action.data.id, action.data.content, action.data.sensitive)
      .pipe(
        map((res) => updateSecretEnvsSuccess(res, action)),
        catchError((res) => of(updateSecretEnvsFail(res, action)))
      )
    )
  ));

  // Bulk-reveal for the multi-edit dialog: fetches all envs of one category as a
  // single `.env` blob. When `sensitive`, the backend requires sudo, so a failure
  // carries a sudo-required error whose `originalAction` is this load action - the
  // global sudo-reauth flow re-dispatches this single action after reauth.
  onLoadUserDataEnvFile$ = createEffect(() => this._actions$.pipe(
    ofType(loadUserDataEnvFile),
    switchMap((action) => this._api
      .getUserDataEnvFile$(action.data.id, action.data.sensitive)
      .pipe(
        map((res) => loadUserDataEnvFileSuccess(res, action)),
        catchError((res) => of(loadUserDataEnvFileFail(res, action)))
      )
    )
  ));

  // Reveals the decrypted value of a sensitive variable. If the backend requires
  // sudo, the failure carries a sudo-required error whose `originalAction` is this
  // reveal action - the global sudo-reauth flow re-dispatches it after reauth.
  //
  // `mergeMap`, not `switchMap`: reveals are per-item and independently triggered
  // (list actions, click-to-reveal, the multi-secret access-details copy flow fires
  // several at once). Switching would silently cancel an earlier item's request and
  // leave it redacted with no error and no spinner.
  onRevealUserData$ = createEffect(() => this._actions$.pipe(
    ofType(revealUserData),
    mergeMap((action) => this._api
      .revealUserData$(action.data.id)
      .pipe(
        map((res) => revealUserDataSuccess(res, action)),
        catchError((res) => of(revealUserDataFail(res, action)))
      )
    )
  ));

  // A reveal that came from clicking the masked value means "I want this value", so
  // hand it over once it lands. It has to be a second, explicit gesture: reveal is
  // async and usually puts the sudo re-auth dialog in between, and browsers reject a
  // clipboard write that isn't inside a user gesture. Centralised here so every
  // surface (env lists, access details, observability credentials) behaves the same.
  onRevealUserDataSuccessOfferCopy$ = createEffect(() => this._actions$.pipe(
    ofType(revealUserDataSuccess),
    filter(({ originalAction }) => !!originalAction?.data?.copyOnSuccess),
    switchMap(({ data }) => this._snack
      .open('Value revealed', 'Copy')
      .onAction()
      .pipe(
        take(1),
        tap(() => this._clipboard.copy(data.content))
      )
    )
  ), { dispatch: false });

  setupUserDataUpdateSubscription$ = createEffect(() => this._userEntity.activeClientId$.pipe(
    map((clientId) => this._userDataEntity.updateSubscribe(clientId),
  )));

  setupUpdateCacheDispatch$ = createEffect(() => this._actions$.pipe(
    onWebsocketMessageDispatchUpdateEntities(this._userDataEntity)
  ));

  constructor(
    private _api: UserDataBaseApi,
    private _userEntity: UserEntity,
    private _userDataEntity: UserDataEntity,
    private _actions$: Actions
  ) { }
}
